import { AnalyticsEventName } from '@dupe/common/constants'
import type { UserCredential } from 'firebase/auth'
import { useCallback, useState } from 'react'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import {
  EmailAuthProvider,
  SupportedProviderId,
  signInWithProvider,
} from '~/src/lib/firebase/firebaseAuth'
import logger from '~/src/lib/logger'

export interface UseAuthCallbackReturn {
  loading: boolean
  authCallback: (
    providerId: SupportedProviderId
  ) => (event: any) => Promise<UserCredential | undefined>
  pendingEmailAuth?: boolean
  clearPendingEmailAuth: () => void
  currentProviderId?: SupportedProviderId
}

const useAuthCallback = (): UseAuthCallbackReturn => {
  const analytics = useAnalytics()
  const { authSource } = useAuthContext()
  const { authUser, loading: loadingAuth } = useAuthUser()
  const [currentProviderId, setCurrentProviderId] = useState<
    SupportedProviderId | undefined
  >()
  const [authCallbackLoading, setAuthCallbackLoading] = useState(false)
  const [pendingEmailAuth, setPendingEmailAuth] = useState<
    boolean | undefined
  >()

  const clearPendingEmailAuth = useCallback(() => {
    setPendingEmailAuth(false)
  }, [])

  const loading = authCallbackLoading || loadingAuth
  const authCallback = useCallback(
    (providerId: SupportedProviderId) =>
      async (event: any): Promise<UserCredential | undefined> => {
        event?.preventDefault()

        setAuthCallbackLoading(true)
        setCurrentProviderId(providerId)

        if (providerId === EmailAuthProvider.PROVIDER_ID) {
          setPendingEmailAuth(true)
          setAuthCallbackLoading(false)
          return
        }

        try {
          const credential = await signInWithProvider({
            providerId,
          })

          setAuthCallbackLoading(false)

          analytics.track(AnalyticsEventName.UserSignedIn, {
            source: authSource,
            method: providerId,
            uid: credential?.user.uid,
            isAnonymous: credential?.user.isAnonymous,
          })

          return credential
        } catch (err: any) {
          logger.error(`authCallback Error: ${err?.message}`, {
            err,
            'user.id': authUser?.uid,
            'context.provider.id': providerId,
          })

          analytics.track(AnalyticsEventName.UserSignedInError, {
            source: authSource,
            error: err?.message,
            'user.id': authUser?.uid,
            'context.provider.id': providerId,
          })
        } finally {
          setAuthCallbackLoading(false)
          setCurrentProviderId(undefined)
        }
      },
    [analytics, authSource, authUser?.uid]
  )

  return {
    loading,
    currentProviderId,
    authCallback,
    pendingEmailAuth,
    clearPendingEmailAuth,
  }
}

export default useAuthCallback
